import { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Stack, Sheet, Typography } from '@mui/joy';
import { useTheme, useMediaQuery } from '@mui/material';
import { useUpdateMealPlanEntryMutation } from '../api';
import { AddMealModal } from './AddMealModal';
import { MealPlanMealTypeSection } from './MealPlanMealTypeSection';
import { MealEntry } from '@/features/meals/components/MealEntry';
import { Label } from '@/ui/Label';
import { DetachedScrollbar } from '@/components/DetachedScrollbar';
import { WeeklyCalendarHeader } from '@/components/calendar/WeeklyCalendarHeader';
import { getWeekDays, localDateToString, getStartOfWeek } from '@/utils/datetime';
import { some } from 'lodash';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { createPortal } from 'react-dom';
import { useRecipesWithPersonalizedNutrition } from '@/features/recipes/hooks/useRecipesWithPersonalizedNutrition';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { resolveMealPlanEntryItem } from '../utils/resolveMealPlanEntryItem';
import { MEAL_TYPES } from '@/features/meals/constants';
import { PAGE_MAX_WIDTH } from '@/theme.js';

// Columns flex to fill the available width -- this is only the floor they
// shrink to before the row starts scrolling instead.
const MIN_DAY_COLUMN_WIDTH = 126;

// The page's own fixed "N meals planned / Build shopping list" bar sits at
// the very bottom (see MealPlanPage) -- these mirror its height and its
// tablet-only shift (to clear the mobile bottom nav) so the detached
// scrollbar below can float just above it instead of underneath it.
const FOOTER_HEIGHT = 75;
const FOOTER_TABLET_SHIFT = 54;
const SCROLLBAR_GAP_ABOVE_FOOTER = 0;

function NextWeekDropZone({ activeId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'next-week-drop-zone',
    data: { isNextWeekZone: true },
  });

  const isDragging = activeId?.startsWith('meal-');
  if (!isDragging) return null;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        width: '100%',
        minHeight: '60px',
        border: '2px dashed',
        borderColor: isOver ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-neutral-300)',
        borderRadius: 'md',
        backgroundColor: isOver ? 'var(--joy-palette-primary-50)' : 'var(--joy-palette-neutral-50)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
      }}
    >
      <Typography
        level="body-xs"
        sx={{
          color: isOver ? 'var(--joy-palette-primary-700)' : 'var(--joy-palette-neutral-500)',          
        }}
      >
        Drop here to move to next week
      </Typography>
    </Box>
  );
}

function DayColumnHeader({ date, isToday }) {
  const dayAbbrev = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();

  return (
    <Stack direction="row" alignItems="baseline" gap={1} sx={{ mb: 1.5 }}>
      <Typography level="h3">{date.getDate()}</Typography>
      <Typography level="title-xs" sx={{ color: 'var(--joy-palette-neutral-500)', letterSpacing: '0.05em' }}>
        {dayAbbrev}
      </Typography>
      {isToday && <Label>Today</Label>}
    </Stack>
  );
}

function DayColumn({ date, entries, recipes, ingredients, events, householdId, isEditable, isDragging }) {
  const [addMealOpen, setAddMealOpen] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState('breakfast');
  const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
  const isToday = date.toDateString() === new Date().toDateString();

  const handleAddMeal = (mealType) => {
    setSelectedMealType(mealType);
    setAddMealOpen(true);
  };

  return (
    <Box sx={{ flex: '1 1 0', minWidth: MIN_DAY_COLUMN_WIDTH }}>
      <DayColumnHeader date={date} isToday={isToday} />
      <Sheet sx={{ borderRadius: 'md', backgroundColor: '#fff', boxShadow: 'rgba(20, 23, 26, 0.06) 0px 1px 3px', p: 1 }}>
        {MEAL_TYPES.map((mealType, index) => (
          <Box
            key={mealType}
            sx={{
              pt: index === 0 ? 0 : 1,
              pb: index === MEAL_TYPES.length - 1 ? 0 : 1,
              mx: -1,
              px: 1,
              borderBottom: index < MEAL_TYPES.length - 1 ? '1px solid var(--joy-palette-neutral-200)' : 'none',
            }}
          >
            <MealPlanMealTypeSection
              isDragging={isDragging}
              mealType={mealType}
              entries={entries}
              recipes={recipes}
              ingredients={ingredients}
              events={events}
              householdId={householdId}
              date={date}
              isEditable={isEditable}
              onAddMeal={handleAddMeal}
              entryVariant="calendar"
              compactAddButton
            />
          </Box>
        ))}
      </Sheet>

      {/* Mounted only while open. There's a modal instance per day, and each
          one filters and sorts the whole recipe list on mount -- keeping
          them all alive would repeat that work once per day on every load. */}
      {addMealOpen && <AddMealModal
        open
        onClose={() => setAddMealOpen(false)}
        date={date}
        dayName={dayName}
        dayNumber={date.getDate()}
        recipes={recipes}
        householdId={householdId}
        isEditable={isEditable}
        mealType={selectedMealType}
      />}
    </Box>
  );
}

function DragOverlayContent({ activeId, mealPlanEntriesCopy, recipes, ingredients, events, columnWidth }) {
  if (!activeId?.startsWith('meal-')) return null;

  const entryId = activeId.replace('meal-', '');
  const entry = mealPlanEntriesCopy?.find((e) => e.id === entryId);
  if (!entry) return null;

  const resolved = resolveMealPlanEntryItem(entry, { recipes, ingredients, events });
  if (!resolved) return null;

  return (
    // px shrinks the card slightly relative to its resting size, matching the
    // same subtle lift used for the drag overlay on mobile (see
    // MobileMealPlanView's DragOverlayContent).
    <Box sx={{ width: columnWidth, px: 0.5 }}>
      <MealEntry
        kind={resolved.kind}
        item={resolved.item}
        entry={entry}
        nutrition={resolved.item.personalizedNutrition}
        isSelected
        variant="calendar"
      />
    </Box>
  );
}

export function DesktopMealPlanView({
  householdId,
  currentWeek,
  weekStartDay,
  mealPlanEntries,
  recipes,
  ingredients,
  events,
  onDateChange,
}) {
  const recipesWithPersonalizedNutrition = useRecipesWithPersonalizedNutrition(recipes);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const scrollContainerRef = useRef(null);
  const columnsRowRef = useRef(null);
  const [columnWidth, setColumnWidth] = useState(MIN_DAY_COLUMN_WIDTH);

  // Columns flex to fill whatever width is available, so the dragged card
  // (rendered outside the grid, in a portal) matches by measuring an actual
  // column instead of guessing its width. The card itself sits inside the
  // column's Sheet, inset by that Sheet's own padding, so it's the Sheet's
  // content box (not the outer column box) that needs to be measured.
  useEffect(() => {
    const row = columnsRowRef.current;
    if (!row) return;
    const updateColumnWidth = () => {
      const sheet = row.firstElementChild?.children[1];
      if (!sheet) return;
      const sheetStyle = getComputedStyle(sheet);
      const horizontalPadding = parseFloat(sheetStyle.paddingLeft) + parseFloat(sheetStyle.paddingRight);
      setColumnWidth(sheet.getBoundingClientRect().width - horizontalPadding);
    };
    updateColumnWidth();
    const resizeObserver = new ResizeObserver(updateColumnWidth);
    resizeObserver.observe(row);
    return () => resizeObserver.disconnect();
  }, []);

  const [mealPlanEntriesCopy, setMealPlanEntriesCopy] = useState([]);

  useEffect(() => {
    setMealPlanEntriesCopy(mealPlanEntries?.data?.data || []);
  }, [mealPlanEntries?.data?.data]);

  const weekDays = useMemo(() => getWeekDays(currentWeek), [currentWeek]);
  const currentWeekDays = useMemo(() => getWeekDays(getStartOfWeek(new Date(), weekStartDay)), [weekStartDay]);

  const entriesByDate = useMemo(() => {
    const byDate = {};
    weekDays.forEach((date) => {
      byDate[localDateToString(date)] = [];
    });
    mealPlanEntriesCopy.forEach((entry) => {
      const entryDate = localDateToString(new Date(entry.date));
      if (byDate[entryDate]) {
        byDate[entryDate].push(entry);
      }
    });
    return byDate;
  }, [mealPlanEntriesCopy, weekDays]);

  // mealPlanEntriesCopy isn't scoped to the displayed week -- entriesByDate is
  // (only dates within weekDays get a key), so sum thaot instead.
  const weekMealCount = useMemo(
    () => Object.values(entriesByDate).reduce((sum, entries) => sum + entries.length, 0),
    [entriesByDate]
  );

  const isDateInCurrentWeek = (date) => {
    return some(currentWeekDays, (day) => day.toDateString() === date.toDateString());
  };

  const isDateEditable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return isDateInCurrentWeek(date) || date >= today;
  };

  const [updateMealPlanEntry] = useUpdateMealPlanEntryMutation();

  const { activeId, sensors, collisionDetection, handleDragStart, handleDragEnd, handleDragCancel } = useDragAndDrop({
    householdId,
    currentWeek,
    weekStartDay,
    mealPlanEntriesCopy,
    setMealPlanEntriesCopy,
    updateMealPlanEntry,
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <Stack gap={2}>
        <Sheet
          variant="plain"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            width: '100vw',
            ml: 'calc(50% - 50vw)',
            px: { xs: 2, md: 3 },
            backgroundColor: '#fff',
            boxShadow: 'rgba(20, 23, 26, 0.05) 0px 1px 3px',
          }}
        >
          <Box sx={{ maxWidth: PAGE_MAX_WIDTH, margin: '0 auto', py: 2 }}>
            <WeeklyCalendarHeader
              mealCount={weekMealCount}
              currentWeekStart={currentWeek}
              onDateChange={onDateChange}
              weekStartDay={weekStartDay}
            />
          </Box>
        </Sheet>
        <Box sx={{ mx: { xs: -2, md: -3}, overflow: 'hidden' }}>
          <Box
            ref={scrollContainerRef}
            sx={{
              overflowX: 'auto',
              overflowY: 'visible',
              pt: 1,
              pb: 1.5,
              px: { xs: 2, md: 3 },
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Stack ref={columnsRowRef} direction="row" gap={1} sx={{ width: '100%', minWidth: 'fit-content' }}>
              {weekDays.map((date) => {
                const dateString = localDateToString(date);
                return (
                  <DayColumn
                    key={dateString}
                    date={date}
                    entries={entriesByDate[dateString] || []}
                    recipes={recipesWithPersonalizedNutrition}
                    ingredients={ingredients}
                    events={events}
                    householdId={householdId}
                    isEditable={isDateEditable(date)}
                    isDragging={activeId != null}
                  />
                );
              })}
            </Stack>
          </Box>
        </Box>
        <NextWeekDropZone activeId={activeId} />
      </Stack>
      <DetachedScrollbar
        scrollRef={scrollContainerRef}
        bottom={`${FOOTER_HEIGHT + SCROLLBAR_GAP_ABOVE_FOOTER + (isTablet ? FOOTER_TABLET_SHIFT : 0)}px`}
      />
      {createPortal(
        <DragOverlay dropAnimation={null}>
          <DragOverlayContent
            activeId={activeId}
            mealPlanEntriesCopy={mealPlanEntriesCopy}
            recipes={recipesWithPersonalizedNutrition}
            ingredients={ingredients}
            events={events}
            columnWidth={columnWidth}
          />
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
}
